// ======================================================================
// THE CLIENT FOR THE CASES API
// ======================================================================
//
// Rewritten for task 11B. The module now lives inside the main Kwery
// application, so the old client's door — /login, /verify, /refresh,
// /logout on a separate origin with its own aud="cases" token — is gone
// along with the endpoints it called. What replaced it is smaller: the
// ordinary Kwery session, plus a step-up marker.
//
// FOUR THINGS IN THIS FILE ARE LOAD-BEARING. The rest is plumbing.
//
//   1. THE FOUR STATUSES ARE FOUR DIFFERENT THINGS AND ARE NEVER
//      COLLAPSED. er_cases/stepup.py's own comment block says why, and
//      the frontend has to hold the same distinction or the block is
//      wasted:
//
//        401                       no Kwery session -> the login redirect
//        403 + STEPUP_REQUIRED     session fine, identity not recently
//                                  proved -> show the MFA prompt
//        404                       the ACL's uniform refusal -> render
//                                  NOTHING
//        503                       the module is unavailable -> say so,
//                                  and do NOT offer the MFA prompt
//
//      The 503 case is the one that bites. During a Redis outage or a
//      schema-gate failure the challenge endpoint cannot run either, so
//      showing the code prompt sends the user into a loop with no exit:
//      request a code, get a 503, try again. It gets its own error class
//      and its own screen.
//
//   2. THE 404 RULE. er_cases/acl.py returns a byte-identical 404 for
//      every refusal it makes — case absent, wrong tenant, no party row,
//      party revoked, capability absent — deliberately, so a caller
//      cannot sort real case ids from noise by status code. A 404 is
//      therefore NOT an error in this application. It is the answer
//      "there is nothing here for you", and the UI's job is to render
//      nothing at all: no error, no empty card with a padlock, no "you
//      do not have permission". Any of those re-creates the 403 the
//      backend spent a module avoiding.
//
//      So get() returns the NOT_ENTITLED sentinel on 404 rather than
//      throwing, and every caller treats it as "omit this section".
//
//   3. REFRESH IS NOT DONE HERE ANY MORE. context/AuthContext.jsx
//      patches window.fetch globally (AuthContext.jsx:218-267): a 401 on
//      our own API triggers a single-flight silent refresh and one
//      retry, with the new token substituted into the headers. Doing it
//      again here would mean two refreshes racing for one refresh token,
//      and the backend blacklists a refresh token on use — the second
//      would fail and sign the user out mid-read. So a 401 that reaches
//      this file is one the interceptor already tried and could not fix,
//      and the only honest response is SessionExpired.
//
//      The upload path is the exception, because it uses XMLHttpRequest
//      and the interceptor only patches fetch. See setRefreshHandler().
//
//   4. NOTHING IS RETRIED. Reads are capped at 60/minute per user and
//      writes at 30/minute (er_cases/routes.py). A client that retries
//      on failure turns one user's bad network into a rate-limit lockout
//      across the whole surface.

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000'

const CASES = `${API_BASE}/api/cases`
const STEPUP = `${CASES}/auth/stepup`

/** The answer to "may I see this?" being no, in the only form the
 *  backend gives it. Not an error. Render nothing. */
export const NOT_ENTITLED = Symbol('cases:not-entitled')

/** The code string er_cases/stepup.py puts in the 403 body. It is
 *  deliberately NOT the "MFA_REQUIRED" that superadmin/account_routes.py
 *  uses for its own prompt: the remedies are different endpoints on
 *  different surfaces, and a client must never satisfy one by answering
 *  the other. */
export const STEPUP_REQUIRED_CODE = 'STEPUP_REQUIRED'

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** The Kwery session is gone and the caller must log in again. Distinct
 *  from ApiError so a section can tell "you are signed out" from "that
 *  write was refused". */
export class SessionExpired extends Error {
  constructor() {
    super('Session expired')
    this.name = 'SessionExpired'
  }
}

/** 403 with {"code": "STEPUP_REQUIRED"}. The session is fine; the person
 *  has not proved recently that they are the account holder. NEVER
 *  treated as a sign-out — doing that would log somebody out of payroll
 *  and leave because they clicked Cases. */
export class StepUpRequired extends Error {
  constructor(body) {
    super(body?.message || 'Confirm your identity to open Cases.')
    this.name = 'StepUpRequired'
    this.body = body || null
  }
}

/** 503. The module cannot serve — Redis is down, or the ER schema gate
 *  failed at boot. Held apart from StepUpRequired because the remedy is
 *  waiting, not answering a code prompt, and because the challenge
 *  endpoint is down too. */
export class ModuleUnavailable extends Error {
  constructor(message) {
    super(message || 'Cases is temporarily unavailable.')
    this.name = 'ModuleUnavailable'
  }
}

// ----------------------------------------------------------------------
// THE SESSION
// ----------------------------------------------------------------------
//
// ONE SESSION NOW, AND THAT IS THE POINT OF TASK 11A. The old client
// kept its own key, "kwery.cases.session", holding an aud="cases" token
// minted by a door that no longer exists. There is nothing to keep
// separate any more: the ER routes take the ordinary Kwery session and
// add a step-up marker, which lives server-side in Redis and is never
// held by this client at all.
//
// Read from localStorage on every call rather than cached, because
// AuthContext rotates it on a silent refresh every fourteen minutes and
// a cached copy would be a stale token roughly as often as it was a
// current one.
function accessToken() {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

// ----------------------------------------------------------------------
// SUBSCRIBERS
// ----------------------------------------------------------------------
//
// A step-up expiry or an outage can surface on ANY request, from any
// section, at any depth. Threading that up through a dozen components
// would mean a dozen chances to drop it. Instead the gate subscribes
// once, and this module tells it — the gate then covers the screen
// WITHOUT NAVIGATING, so the user's place is kept and the same URL is
// still underneath when they finish.
let stepUpListener = null
let unavailableListener = null

export function onStepUpRequired(fn) {
  stepUpListener = fn
  return () => {
    if (stepUpListener === fn) stepUpListener = null
  }
}

export function onModuleUnavailable(fn) {
  unavailableListener = fn
  return () => {
    if (unavailableListener === fn) unavailableListener = null
  }
}

/** Set by the gate from AuthContext's single-flight silentRefresh, for
 *  the ONE path window.fetch's interceptor cannot see: the XHR upload.
 *  Reusing AuthContext's promise rather than refreshing independently is
 *  what keeps two refreshes from racing for one blacklisted token. */
let refreshHandler = null
export function setRefreshHandler(fn) {
  refreshHandler = fn
}

// ----------------------------------------------------------------------
// STEP-UP
// ----------------------------------------------------------------------
//
// These three take the ordinary session and no marker — they are how a
// marker is obtained. They live on the entry router (/api/cases/auth),
// which carries no require_stepup dependency.

async function stepupCall(path, { method = 'GET', body } = {}) {
  const token = accessToken()
  if (!token) throw new SessionExpired()

  let res
  try {
    res = await fetch(`${STEPUP}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    throw new ApiError(0, 'network')
  }

  const parsed = await readBody(res)
  if (res.status === 401) throw new SessionExpired()
  if (res.status === 503) throw new ModuleUnavailable(detailOf(parsed))
  if (!res.ok) {
    throw new ApiError(res.status, detailOf(parsed) || defaultMessageFor(res.status), parsed)
  }
  return parsed
}

export const stepup = {
  /** { verified, expires_in }. Answers false rather than 503 when Redis
   *  is down — it is a probe used to decide whether to draw a prompt,
   *  and the real request is where an outage should surface. */
  status: () => stepupCall('/status'),
  /** { expires_in, sent_to }. sent_to is REDACTED by the backend: the
   *  screen in an HR office is read by whoever is standing behind it. */
  request: () => stepupCall('/request', { method: 'POST', body: {} }),
  /** { verified, expires_in, absolute_expires_in }. */
  verify: (code) => stepupCall('/verify', { method: 'POST', body: { code } }),
}

// ----------------------------------------------------------------------
// THE AUTHENTICATED CLIENT
// ----------------------------------------------------------------------

async function raw(path, { method = 'GET', body, formData, signal } = {}) {
  const token = accessToken()
  if (!token) throw new SessionExpired()

  const headers = { Authorization: `Bearer ${token}` }
  let payload
  if (formData) {
    // Content-Type is deliberately NOT set: the browser must generate
    // the multipart boundary itself.
    payload = formData
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  let res
  try {
    // AuthContext's interceptor sits under this call and has already
    // done a refresh-and-retry if the first attempt 401'd. See (3).
    res = await fetch(`${CASES}${path}`, { method, headers, body: payload, signal })
  } catch (e) {
    if (e?.name === 'AbortError') throw e
    throw new ApiError(0, 'network')
  }

  if (res.status === 401) throw new SessionExpired()

  if (res.status === 503) {
    const err = new ModuleUnavailable(detailOf(await peek(res)))
    if (unavailableListener) unavailableListener(err)
    throw err
  }

  if (res.status === 403) {
    const parsed = await peek(res)
    // THE SHAPE IS CHECKED, NOT THE STATUS. A 403 that is not this one
    // is a different refusal and must not raise the code prompt — a
    // prompt that cannot fix the problem is a loop with a keypad on it.
    const detail = parsed?.detail
    if (detail?.code === STEPUP_REQUIRED_CODE) {
      const err = new StepUpRequired(detail)
      if (stepUpListener) stepUpListener(err)
      throw err
    }
    throw new ApiError(403, detailOf(parsed) || defaultMessageFor(403), parsed)
  }

  return res
}

// Reads the body without consuming it for the caller, by buffering the
// text and re-attaching it. Only used on the error paths above, where
// the response is not returned to anybody.
async function peek(res) {
  try {
    return await readBody(res)
  } catch {
    return null
  }
}

async function readBody(res) {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/** FastAPI puts the message in `detail`, which is a string for most
 *  refusals and an object for the step-up 403. Never assume either. */
function detailOf(parsed) {
  if (!parsed) return null
  const d = parsed.detail
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message
  return null
}

/** A read. Returns NOT_ENTITLED on 404 — see the 404 rule above. */
export async function get(path, opts) {
  const res = await raw(path, opts)
  if (res.status === 404) return NOT_ENTITLED
  const body = await readBody(res)
  if (!res.ok) throw new ApiError(res.status, detailOf(body) || res.statusText, body)
  return body
}

/** A write. A 404 here is NOT swallowed.
 *
 *  The asymmetry is deliberate. On a read, 404 means "this section does
 *  not exist for you" and the honest render is nothing. On a write, the
 *  user pressed a button and is owed an answer; silently doing nothing
 *  would leave them pressing it again. The message shown is the
 *  backend's own — never a guess at which of the refusal causes it was,
 *  because the response does not say and inventing one would be a lie
 *  in a register somebody may later rely on. */
export async function write(path, { method = 'POST', body, formData } = {}) {
  const res = await raw(path, { method, body, formData })
  const parsed = await readBody(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      detailOf(parsed) || defaultMessageFor(res.status),
      parsed,
    )
  }
  return parsed
}

function defaultMessageFor(status) {
  if (status === 403) return 'That was refused.'
  if (status === 404) return 'That is no longer available.'
  if (status === 429) return 'Too many requests. Wait a moment and try again.'
  if (status === 503) return 'Cases is temporarily unavailable.'
  return 'That could not be completed.'
}

// ----------------------------------------------------------------------
// EVIDENCE UPLOAD — XHR, NOT FETCH
// ----------------------------------------------------------------------
//
// fetch() gives no upload progress. There is no onUploadProgress, no
// readable request stream in any shipping browser, and no way to know
// how far a 250 MB interview recording has got. XMLHttpRequest still
// does, so uploads use it and nothing else does.
//
// The cap is 250 MB (blob.ER_MAX_UPLOAD_BYTES) and is enforced in
// blob.put() AFTER the handler is entered, so an oversized file is
// refused with an audited 400 rather than a framework rejection. This
// client does not pre-check the size: doing so would refuse the upload
// with no audit row, which is the exact defect the backend's
// permissive-transport design exists to avoid.
function upload(path, formData, { onProgress, token } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${CASES}${path}`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total)
    }
    xhr.onerror = () => reject(new ApiError(0, 'network'))
    xhr.onabort = () => reject(new ApiError(0, 'aborted'))
    xhr.onload = () => {
      let parsed = null
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        parsed = null
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed)
        return
      }
      if (xhr.status === 503) {
        const err = new ModuleUnavailable(detailOf(parsed))
        if (unavailableListener) unavailableListener(err)
        reject(err)
        return
      }
      if (xhr.status === 403 && parsed?.detail?.code === STEPUP_REQUIRED_CODE) {
        // A long upload can outlive a step-up marker — a 200 MB recording
        // on office broadband takes minutes and the idle window is not
        // measured in minutes of silence for nothing. The prompt goes up
        // and the file is still selected underneath.
        const err = new StepUpRequired(parsed.detail)
        if (stepUpListener) stepUpListener(err)
        reject(err)
        return
      }
      reject(
        new ApiError(
          xhr.status,
          detailOf(parsed) || defaultMessageFor(xhr.status),
          parsed,
        ),
      )
    }
    xhr.send(formData)
  })
}

/** Upload with the one-retry-after-refresh behaviour the fetch path gets
 *  from AuthContext's interceptor. Split out because XHR is invisible to
 *  a window.fetch patch, and it defers to AuthContext's single-flight
 *  refresh rather than starting a second one. */
export async function uploadWithRefresh(path, formData, { onProgress } = {}) {
  const token = accessToken()
  if (!token) throw new SessionExpired()
  try {
    return await upload(path, formData, { onProgress, token })
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      if (!refreshHandler) throw new SessionExpired()
      const fresh = await refreshHandler()
      if (!fresh) throw new SessionExpired()
      return upload(path, formData, { onProgress, token: fresh })
    }
    throw e
  }
}

// ----------------------------------------------------------------------
// EVIDENCE DOWNLOAD — A NORMAL AUTHENTICATED REQUEST
// ----------------------------------------------------------------------
//
// THERE IS NO SIGNED URL AND THERE MUST NOT BE. The download route is
// behind require_case and resolves the ACL per request, so revoking a
// party's access takes effect on their next click. A pre-signed URL
// would make possession of the link sufficient: it survives revocation,
// works from any machine with no session, and lands in browser history
// and in any chat where somebody pastes "the link".
//
// So the file is fetched with the Authorization header like everything
// else and handed to the browser as a blob. The server always sends
// Content-Disposition: attachment with X-Content-Type-Options: nosniff
// and application/octet-stream, which is what makes accepting every file
// type safe; the anchor's `download` attribute below preserves that —
// the browser saves rather than renders, including for a .html exhibit.
//
// ----------------------------------------------------------------------
// THE RESPONSE HEADERS MAY NOT BE READABLE. STILL A BACKEND CORS GAP.
// ----------------------------------------------------------------------
// Kwery_backend/main.py builds CORSMiddleware without `expose_headers`,
// so a cross-origin script may read only the seven safe-listed response
// headers and none of the three this route sends for the client's
// benefit is among them: Content-Disposition (the filename),
// Content-Length (the total, for a percentage) and X-ER-Content-SHA256.
//
// The browser receives all three and applies them; the script cannot see
// them. That is why `filename` and `totalBytes` are passed IN by the
// caller from the evidence row it is already holding.
//
// TASK 11B NOTE: in development the app is served from localhost:3000
// and the API from localhost:8000, so this still applies. It stops
// applying the moment both are same-origin — which is now possible, and
// was not while the module was a separate app. The one-argument fix in
// main.py is still the right one, and is still not this task's to make.
export async function download(path, { onProgress, filename, totalBytes } = {}) {
  const res = await raw(path)
  if (res.status === 404) return NOT_ENTITLED
  if (!res.ok) {
    const body = await readBody(res)
    throw new ApiError(res.status, detailOf(body) || defaultMessageFor(res.status), body)
  }

  const name = filenameFrom(res.headers.get('Content-Disposition')) || filename
  const total = Number(res.headers.get('Content-Length')) || totalBytes || 0

  // Streamed and reassembled so a 250 MB recording reports progress
  // instead of sitting on a spinner for two minutes.
  let blob
  if (res.body && total > 0 && onProgress) {
    const reader = res.body.getReader()
    const chunks = []
    let received = 0
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      received += value.length
      onProgress(received / total)
    }
    blob = new Blob(chunks)
  } else {
    blob = await res.blob()
  }

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name || 'download'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoked on the next tick: revoking synchronously races the click in
  // Safari and produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
  return { filename: name, bytes: blob.size }
}

function filenameFrom(header) {
  if (!header) return null
  // RFC 6266: the server sends both forms. Prefer filename*, which
  // survives non-ASCII names.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (star) {
    try {
      return decodeURIComponent(star[1])
    } catch {
      /* fall through to the ASCII form */
    }
  }
  const plain = /filename="([^"]*)"/i.exec(header)
  return plain ? plain[1] : null
}
